import { slugify } from "@chef/helpers";

type SearchMethod = "bfs" | "dfs";

type Key = string | number;

class TreeNode {
  key: Key;
  ids: Key[];
  value: Key;
  slug: string;
  parent: TreeNode | null;
  children: TreeNode[];
  node: TreeNode = this;

  constructor(key: Key, value = key, parent: TreeNode | null = null) {
    this.key = key;
    this.ids = [key];
    this.value = value;
    this.slug = slugify(value.toString());
    this.parent = parent;
    this.children = [];
  }

  get isLeaf() {
    return this.children.length === 0;
  }

  get hasChildren() {
    return !this.isLeaf;
  }

  get ancestors() {
    const ancestors: TreeNode[] = [];

    let current: TreeNode | null = this.node;

    while (current) {
      ancestors.unshift(current);
      current = current.parent;
    }

    return ancestors;
  }

  addId(id: Key) {
    this.ids.push(id);
  }
}

export class Tree {
  root: TreeNode;

  constructor(key?: string, value = key) {
    this.root = new TreeNode(key ?? "root", value);
  }

  get paths() {
    const paths: Key[][] = [];

    const buildPaths = (node: TreeNode, parentPath: Key[] = []): Key[][] => {
      const currentPath = [...parentPath, node.slug];

      const nodePaths = [currentPath];

      node.children.forEach((child) => {
        nodePaths.push(...buildPaths(child, currentPath));
      });
      return nodePaths;
    };

    this.root.children.forEach((child) => {
      paths.push(...buildPaths(child));
    });

    return paths;
  }

  visualize() {
    const visualizeNode = (node: TreeNode, depth = 0) => {
      console.log(
        "visualization of tree",
        `${" ".repeat(depth)}${`${node.value} (${node.ids.join(", ")})`}`,
      );
      node.children.forEach((child) => visualizeNode(child, depth + 2));
    };
    visualizeNode(this.root);
  }

  find(key: Key | null, method: SearchMethod = "bfs") {
    if (!key) {
      return null;
    }
    const queue = [this.root];
    while (queue.length) {
      const node = method === "bfs" ? queue.shift()! : queue.pop()!;
      if (node.key === key) {
        return node;
      }
      queue.push(...node.children);
    }

    return null;
  }

  findBySlug(slug: string, rootSlug: string) {
    if (typeof slug !== "string") {
      return null;
    }

    if (slug === rootSlug) {
      return this.root;
    }

    const slugParts = slug.split("/");
    const queue = [this.root];

    while (queue.length && slugParts.length) {
      const part = slugParts.shift();
      const nextQueue = [];
      while (queue.length) {
        const node = queue.shift()!;
        const childNode = node.children.find((child) => child.slug === part);
        if (childNode) {
          if (slugParts.length === 0) {
            return childNode;
          }
          nextQueue.push(childNode);
        }
      }
      queue.push(...nextQueue);
    }
    return null;
  }

  insert(parentKey: Key | null, key: Key, value = key) {
    const parent = this.find(parentKey) ?? this.root;
    parent.children.push(new TreeNode(key, value, parent));
  }

  insertAndCoalesceByValue(
    parentKey: Key | null,
    key: Key,
    value = key,
    rootSlug: string,
  ) {
    const node = this.findBySlug(slugify(value.toString()), rootSlug);

    if (node) {
      node.addId(key);
    } else {
      this.insert(parentKey, key, value);
    }
  }
}
